import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProjectMember } from "@/types/Project";
import { useToast } from "@/components/ui/use-toast";

export function useProjectMembers(projectId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await supabase
        .from("project_members")
        .select(`
          *,
          profiles (
            full_name,
            email,
            avatar_url
          )
        `)
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data.map(member => ({
        id: member.id,
        project_id: member.project_id,
        user_id: member.user_id,
        role: member.role as 'owner' | 'admin' | 'member' | 'viewer',
        invited_by: member.invited_by,
        invited_at: member.invited_at ? new Date(member.invited_at) : undefined,
        joined_at: member.joined_at ? new Date(member.joined_at) : undefined,
        created_at: new Date(member.created_at),
        updated_at: new Date(member.updated_at),
        full_name: (member.profiles as any)?.full_name,
        email: (member.profiles as any)?.email,
        avatar_url: (member.profiles as any)?.avatar_url,
      })) as ProjectMember[];
    },
    enabled: !!projectId,
  });

  const inviteMember = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: 'admin' | 'member' | 'viewer' }) => {
      // First, find the user by email
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email)
        .single();

      if (profileError || !profile) {
        throw new Error("User not found. They must have an account first.");
      }

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("project_members")
        .insert({
          project_id: projectId,
          user_id: profile.id,
          role,
          invited_by: user.user.id,
          invited_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
      toast({
        title: "Member invited",
        description: "The team member has been successfully invited to the project.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error inviting member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMemberRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: 'admin' | 'member' | 'viewer' }) => {
      const { data, error } = await supabase
        .from("project_members")
        .update({ role, updated_at: new Date().toISOString() })
        .eq("id", memberId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
      toast({
        title: "Role updated",
        description: "The member's role has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from("project_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
      toast({
        title: "Member removed",
        description: "The member has been removed from the project.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error removing member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const convertToTeamProject = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .update({ is_team_project: true })
        .eq("id", projectId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
      toast({
        title: "Project converted",
        description: "Project has been converted to a team project.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error converting project",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    members,
    isLoading,
    inviteMember: inviteMember.mutateAsync,
    updateMemberRole: updateMemberRole.mutateAsync,
    removeMember: removeMember.mutateAsync,
    convertToTeamProject: convertToTeamProject.mutateAsync,
  };
}